import classnames from "classnames";
import omit from 'omit.js';
import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import { WebUI, cssUtil, getNid, prefix } from "../../wui-core/src/index";
import Icon from '../../wui-icon/src';
import Input from '../../wui-input/src/index';
import { getLangInfo } from "../../wui-locale/src/tool";
import { WithConfigConsumer } from "../../wui-provider/src/context";
import Select from '../../wui-select/src/index';
import i18n from './i18n.js';
import { PaginationButtonAdapter, PaginationButtonAdapterPublic, PaginationProps, PaginationState } from './iPagination';
import PaginationButton from "./PaginationButton";

const Option = Select.Option;

const sizeChangerPropsMap: string[] = ['dropdownClassName', 'dropdownStyle', 'fieldid', 'id', 'listHeight', 'placement', 'dropdownAlign', 'suffixIcon', 'getPopupContainer']

const getCache = (key: string) => {
    try {
        return JSON.parse(window.localStorage.getItem(key) || '{}'); // 缓存的分页信息
    } catch {
        return {};
    }
};

const getPopupContainer = (dom: HTMLElement) => {
    // selectDomRef.current = dom;
    // this.setListHeight();
    return cssUtil.parentsUntil(dom);
};

const getPageButtonMap = (dir: string | undefined) => ([
    {type: 'first' as PageButtonMapType, children: <Icon type={dir === 'rtl' ? "uf-2arrow-right" : "uf-2arrow-left"}></Icon>, isSimple: false, title: '返回首页'},
    {type: 'prev' as PageButtonMapType, children: <Icon type={dir === 'rtl' ? "uf-arrow-right" : "uf-arrow-left"}></Icon>, isSimple: true, title: '上一页'},
    {type: 'center' as PageButtonMapType, isSimple: true},
    {type: 'next' as PageButtonMapType, children: <Icon type={dir === 'rtl' ? "uf-arrow-left" : "uf-arrow-right"}></Icon>, isSimple: true, title: '下一页'},
    {type: 'last' as PageButtonMapType, children: <Icon type={dir === 'rtl' ? "uf-2arrow-left" : "uf-2arrow-right"}></Icon>, isSimple: false, title: '跳至尾页'},
])

export const defaultProps: PaginationProps = {
    defaultActivePage: 1,
    defaultPageSize: 10,
    maxButtons: 5,
    boundaryLinks: true,
    ellipsis: true,
    first: true,
    last: true,
    prev: true,
    next: true,
    showQuickJumper: true,
    total: 0,
    disabled: false,
    // sizeWithCookie: '',
    hideOnSinglePage: false,
    // responsive:false,
    showTitle: true,
    showSizeChanger: false,
    gap: true,
    noBorder: true,
    size: 'sm',
    pageSizeInput: false,
    pageSizeOptions: [
        '10',
        '20',
        '30',
        '50',
        '100',
        '200',
        '500',
        '1000',
    ],
};
type PageButtonMapType = 'first' | 'prev' | 'center' | 'next' | 'last';

const Pagination = forwardRef<any, PaginationProps>((props, ref) => {
    const { buttonComponentClass, noBorder = true, className, style, clsPrefix, size = "sm", gap = true, pageSizeOptions = defaultProps.pageSizeOptions, itemRender, showQuickJumper = true,
        total = 0, disabled = false, confirmBtn, showTotal, showTitle = true, hideOnSinglePage = false, showSizeChanger = false, locale, simple, dropdownAlign, id, fieldid,
        pageSizeInput = false, sizeChangerProps = {}, cacheId, onKeyDown, dir, pageSize, defaultPageSize = 10, activePage, defaultActivePage = 1,
        items, onChange, onPageSizeChange, ellipsis = true, maxButtons = 5, boundaryLinks = true, first = true, prev = true, next = true, last = true, ...others } = props;

    // 获取缓存数据的辅助函数
    const getCacheData = () => {
        if (cacheId && typeof cacheId === 'string') {
            return getCache(cacheId)
        }
    };

    // 获取语言配置的辅助函数
    const lang = useMemo(() => {
        return getLangInfo(locale, i18n, 'pagination')
    }, [locale])

    // 初始状态
    const [state, setState] = useState<PaginationState>(() => {
        let pageSizeTemp = getCacheData()?.pageSize || (pageSize ?? defaultPageSize) as number;
        let activePageTemp = (activePage ?? defaultActivePage) as number;
        return {
            activePage: activePageTemp, // 当前的页码
            pageSize: pageSizeTemp,
            items: items ? items : total ? Math.ceil(total / pageSizeTemp) : 1,
            jumpPageState: simple ? activePageTemp : null,
            // 分页大小下拉框是否展开，兼容select enter 操作影响其展开收起
            // listHeight: 256, // select 下拉框默认高度
            loading: total === Infinity, // total为Infinity时，分页处于加载状态
        };
    });
    const [selectOpen, setSelectOpen] = useState<boolean>(false);
    const [selectFocus, setSelectFocus] = useState<boolean>(false);
    const [blurIsValid, setBlurIsValid] = useState<boolean>(false);

    // 使用 useRef 保存一些引用
    // const selectDomRef = useRef<HTMLElement | null>(null);

    // 使用 useImperativeHandle 暴露 getCache 方法给父组件
    useImperativeHandle(ref, () => ({
        getCache: () => getCacheData()
    }), [cacheId]);

    // 缓存页面信息
    const cachePageInfo = () => {
        const { pageSize } = state;
        if (cacheId && typeof cacheId === 'string') {
            const cacheInfo = getCache(cacheId);
            if (pageSize && pageSize !== cacheInfo.pageSize) {
                window.localStorage.setItem(cacheId, JSON.stringify({pageSize}))
            }
        }
    };

    // 替换 componentDidMount, 和componentDidUpdate重复
    // useEffect(() => {
    // 	cachePageInfo();
    // }, []);

    // 替换 componentDidUpdate，只在 pageSize 变化时缓存
    useEffect(() => {
        cachePageInfo();
    }, [cacheId, state.pageSize]);

    // 替换 getDerivedStateFromProps 逻辑
    const prevSimpleRef = useRef(simple);
    useEffect(() => {
        setState(prevState => {
            const {activePage: stateActivePage, pageSize: statePageSize, items: stateItems, jumpPageState} = prevState;
            const partialState: any = {};
            // 处理 activePage 变化
            if (activePage && stateActivePage !== activePage) {
                partialState.activePage = activePage;
            }

            // 处理 pageSize 变化
            if (!isNaN(pageSize as number) && statePageSize !== pageSize) {
                partialState.pageSize = pageSize as number;
                partialState.items = items ? items : (Math.ceil(total! / (pageSize as number)) || 1);
            }

            // 处理 simple 模式下的 jumpPageState
            if (simple && activePage && stateActivePage !== activePage) {
                partialState.jumpPageState = activePage;
            }
            if (simple && !prevSimpleRef.current) {
                partialState.jumpPageState = activePage || stateActivePage;
            }

            // 处理 items 变化
            if (items && stateItems !== items) {
                partialState.items = items === 0 ? 1 : items;
            }

            // 处理 total 变化
            if (total !== undefined) {
                const pageSizeValue = pageSize ?? statePageSize;
                const newItems = Math.ceil(total / (pageSizeValue as number)) || 1;
                partialState.items = newItems;
                partialState.loading = total === Infinity;

                // 调整 activePage 如果超出范围
                if (activePage === undefined) { // 仅在非受控模式下调整
                    partialState.activePage = stateActivePage > newItems ? 1 : stateActivePage; // 解决total改变后,activePage和jumpPageState超出页数的问题
                }

                // 调整 jumpPageState 如果超出范围
                if (jumpPageState !== null && Number(jumpPageState) > newItems) {
                    partialState.jumpPageState = null;
                }
            }
            prevSimpleRef.current = simple;
            return { ...prevState, ...partialState };
        })
    }, [activePage, total, simple, pageSize, items, state.pageSize]);

    // 记录跳转页码jumpPageState
    const setPageJump = useCallback((value: string | number) => {
        // 20181129跳转内容可以清空
        if (value !== '' && (isNaN(Number(value)) || +value > state.items || +value <= 0)) {
            return false;
        }
        setState(prevState => ({
            ...prevState,
            jumpPageState: Number(value)
        }));
    }, [state.items]);

    /**
	 * 确认跳页
	 */
    const handleEnsurePageJump = useCallback(() => {
        const {jumpPageState, pageSize} = state;
        if (!jumpPageState) return;
        // 非simple 格式的输入框内容清空
        if (!simple) {
            setState(prevState => ({
                ...prevState,
                jumpPageState: null,
            }));
        }
        // activePage完全受控，外部传了activePage，不走内部切换页逻辑
        if (activePage === undefined) {
            setState(prevState => ({
                ...prevState,
                activePage: jumpPageState,
            }));
        }
        onChange && typeof onChange === 'function' && onChange(jumpPageState, pageSize)
    }, [simple, activePage, onChange, state.jumpPageState, state.pageSize]);

    /**
	 * 分页按钮选择
	 */
    const onPageBtnSelect = useCallback((eventKey: number, pageSize: number, event: React.MouseEvent<HTMLElement>) => {
        if (activePage === undefined) {
            setState(prevState => ({ ...prevState, activePage: eventKey }));
        }
        onChange && onChange(eventKey, pageSize, event)
    }, [onChange, activePage]);

    /**
	 * 每页多少条的选择
	 * @param value
	 */
    const dataNumSelect = useCallback((value: number | string) => {
        let {activePage: currentActivePage, jumpPageState} = state;
        let pageSizeValue = Number(value) || pageSizeOptions[0] as number;
        // 对数组已经排序，最后一位就是最大值
        let maxPageSize = pageSizeOptions[pageSizeOptions.length - 1] as number;
        if (pageSizeInput && typeof pageSizeInput === 'object') {
            maxPageSize = pageSizeInput.max || maxPageSize;
        }
        if (pageSizeValue <= 0) pageSizeValue = pageSizeOptions[0] as number;
        if (pageSizeValue > maxPageSize) pageSizeValue = maxPageSize;

        const newItems = Math.ceil(total! / pageSizeValue); // 调整pageSize后，新的总页数
        const newState: any = {};
        if (total) {
            newState.items = isNaN(pageSizeValue) ? 1 : newItems;
            if (currentActivePage > newItems) {
                currentActivePage = newItems;
                if (activePage === undefined) {
                    newState.activePage = currentActivePage;
                }
            }
        }
        // QDJCJS-11445 在调整pageSize后, 应清空跳页的输入框中不合法的数字
        if (jumpPageState !== null && jumpPageState > newItems) {
            newState.jumpPageState = null;
        }
        if (pageSize === undefined) {
            newState.pageSize = pageSizeValue;
        }
        if (Object.keys(newState).length) {
            setState(prevState => ({...prevState, ...newState}));
        }
        setSelectOpen(false)
        setBlurIsValid(false)
        const res = onPageSizeChange && typeof onPageSizeChange === 'function' && onPageSizeChange(currentActivePage, pageSizeValue);
        if (res) { // QDJCJS-23659 修复分页大小调整后，用户回调同样的pageSize，导致页码错误的问题
            const { pageSize: resPageSize, activePage: resActivePage } = res;
            if (resPageSize) {
                const items = Math.ceil(total! / (resPageSize as number)) || 1;
                setState(prevState => ({
                    ...prevState,
                    pageSize: resPageSize,
                    items,
                    activePage: resActivePage || (prevState.activePage > items ? 1 : prevState.activePage),
                }));
            }
        }
    }, [onPageSizeChange, total, pageSizeOptions, pageSizeInput, activePage, pageSize, state.activePage, state.jumpPageState]);

    /**
	 * 分页大小输入框键盘操作
	 */
    const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement> & React.ChangeEvent<HTMLInputElement>) => {
        // pageSizeOptions.includes(e.target.value) && selectOpen兼容分页下拉框展示的时候且输入下拉框单含的值的时候触发两次问题
        if (e && e.key === 'Enter' && !(pageSizeOptions.includes(e.target.value) && selectOpen)) {
            // 验证输入值是否为有效数字
            const value = e.target.value;
            if (value && !isNaN(Number(value)) && Number(value) > 0) {
                dataNumSelect(value);
            }
        }
    }, [pageSizeOptions, selectOpen, dataNumSelect]);

    /**
	 * 分页大小框点击，控制下拉框展开收起,当下拉展开selectFocus为true
	 */
    const handleSelectClick = () => {
        setSelectOpen(!selectOpen)
        setSelectFocus(!selectOpen)
    };

    /**
	 * 分页大小框失焦，控制下拉框收起
	 */
    const handleSelectBlur = useCallback(() => {
        const { pageSize } = state;
        if (pageSizeInput && blurIsValid) {
            setSelectOpen(false)
            setSelectFocus(false)
            dataNumSelect(pageSize);
            return;
        }
        setSelectOpen(false)
        setSelectFocus(false)
    }, [pageSizeInput, state.pageSize, blurIsValid, dataNumSelect]);

    // pageSizeInput为true时，select为combox模式，实时更新输入值
    const handleSelectChange = (value: string | number) => {
        // 禁止输入非法字符&小数
        if (isNaN(Number(value)) || Math.floor(Number(value)) !== Number(value) || value.toString().match(/\D/g)) {
            return;
        }
        if (pageSizeInput) {
            setState(prevState => ({ ...prevState, pageSize: Number(value) }));
        }
        setSelectFocus(false)
        setBlurIsValid(true)
    };

    const handleSelectFocus = () => {
        if (pageSizeInput) {
            setSelectFocus(true)
        }
    };

    /**
	 * 不同类型的PaginationButton定义渲染
	 * @param type 类型
	 * @param children 渲染内容
	 * @param display 是否渲染出来
	 * @param params 其他参数
	 * @returns {Array}
	 */
    const getPaginationButtonAdapter = useCallback(({children, type, display = true, ...params}: PaginationButtonAdapter): React.ReactNode => {
        const {activePage, items, loading} = state;
        let itemProps = {...params, type}
        switch (type) {
            case 'ellipsis':
                itemProps = {...itemProps, disabled: true, className: `${clsPrefix}-item-ellipsis`};
                children = (
                    <span aria-label="More">
                        {ellipsis === true ? "\u2026" : ellipsis}
                    </span>
                );
                display = display && !loading;
                break;
            case 'first':
                itemProps = {...itemProps, eventKey: 1, disabled: activePage === 1};
                break;
            case 'prev':
                itemProps = {...itemProps, eventKey: activePage - 1, disabled: activePage === 1};
                break;
            case 'next':
                itemProps = {...itemProps, eventKey: activePage + 1, disabled: activePage >= items};
                break;
            case 'last':
                itemProps = {...itemProps, eventKey: items, disabled: loading || activePage >= items};
                break;
            case "page": {
                const {pagenumber} = itemProps;
                children = pagenumber;
                itemProps = {
                    ...omit(itemProps, ["pagenumber"]),
                    eventKey: pagenumber,
                    active: activePage === pagenumber,
                    title: pagenumber?.toString(),
                    key: pagenumber
                };
                display = display && (!loading || pagenumber === activePage);
                break;
            }
            default:
                break;
        }
        return (
            display ? <PaginationButton {...itemProps}>{children || ''}</PaginationButton> : null
        )
    }, [clsPrefix, ellipsis, state.activePage, state.items, state.loading]);

    /**
	 * 渲染page的页码按钮
	 * @param buttonProps
	 * @returns {Array}
	 */
    const renderPageButtons = useCallback((buttonProps: PaginationButtonAdapterPublic) => {
        const {activePage, items, loading} = state;
        let pageButtons = [];

        let startPage;
        let endPage;
        let hasHiddenPagesAfter;

        if (maxButtons) {
            // 根据max很当前activepage计算出应隐藏activeButton之前的页数
            let hiddenPagesBefore = activePage - parseInt(`${maxButtons / 2}`, 10);
            startPage = hiddenPagesBefore >= 2 ? hiddenPagesBefore : 1;
            // 计算出是否存在隐藏activeButton之后的页数
            hasHiddenPagesAfter = startPage + maxButtons < items + 1;

            if (!hasHiddenPagesAfter) {
                endPage = items;
                startPage = items - maxButtons + 1;
                if (startPage < 1) {
                    startPage = 1;
                }
            } else {
                endPage = startPage + maxButtons - 1;
            }
        } else {
            startPage = 1;
            endPage = items;
        }
        // 将所有的button循环渲染出来
        for (let pagenumber = startPage; pagenumber <= endPage; pagenumber++) {
            pageButtons.push(
                getPaginationButtonAdapter({
                    ...buttonProps,
                    type: "page",
                    pagenumber: pagenumber
                })
            );
        }
        if (loading) {
            pageButtons.push(
                getPaginationButtonAdapter({
                    ...buttonProps,
                    type: "loading",
                    children: <i className="uf-circle-loading" />,
                    title: lang.langMap.loading || '加载中...',
                    disabled: true,
                    simple: true,
                    key: "loading",
                    className: `${clsPrefix}-loading`,
                })
            );
        }
        // 如果boundaryLinks和eclipsis且startPage!=1 需要加上before More Button
        if (boundaryLinks && ellipsis && startPage !== 1) {
            if (startPage > 2) {
                pageButtons.unshift(
                    getPaginationButtonAdapter({
                        type: "ellipsis",
                        key: "ellipsisFirst",
                        componentClass: buttonProps.componentClass,
                        iconBtn: false,
                    })
                );
            }
            // 加上最小边界 Button
            pageButtons.unshift(
                getPaginationButtonAdapter({
                    ...buttonProps,
                    type: "page",
                    pagenumber: 1,
                })
            );
        }
        // 如果maxButtons和eclipsis且hasHiddenPagesAfter 需加上after More Button
        if (maxButtons && hasHiddenPagesAfter && ellipsis) {
            if (!boundaryLinks || (boundaryLinks && (items > 1 + endPage))) {
                pageButtons.push(
                    getPaginationButtonAdapter({
                        type: "ellipsis",
                        key: "ellipsisLast",
                        componentClass: buttonProps.componentClass,
                        iconBtn: false,
                    })
                );
            }
            // 如果最后一个页数按钮不等于总页数 且 边界为true 需加上最大边界按钮
            if (boundaryLinks && endPage !== items) {
                pageButtons.push(
                    getPaginationButtonAdapter({
                        ...buttonProps,
                        type: "page",
                        pagenumber: items,
                    })
                );
            }
        }

        return pageButtons;
    }, [state.activePage, state.items, state.loading, lang?.langMap?.loading, maxButtons, boundaryLinks, ellipsis, clsPrefix, getPaginationButtonAdapter]);

    const pageButtonMap = useMemo(() => getPageButtonMap(dir), [dir]);
    /**
	 * 渲染page的所有按钮
	 * @param buttonProps
	 * @param local
	 * @returns {Array}
	 */
    const renderPageList = (buttonProps: PaginationButtonAdapterPublic, local: any): Array<React.ReactNode> => {
        const {jumpPageState, pageSize} = state;
        return pageButtonMap.map(item => {
            if (!item.isSimple && simple) return null;
            if (item.type === 'center') {
                return simple ? (typeof simple !== 'boolean' && !simple?.showQuickJumper) ? null
                    : (<div key="simple-center" className="page_jump page_jump_simple">
                        <Input
                            className="page_jump_value page_jump_value_simple"
                            size={size === 'lg' ? 'lg' : 'xs'} // pagination只有两个size：sm=22和lg=40，与表单组件统一尺寸不一致，需特殊处理
                            value={jumpPageState || ''}
                            onPressEnter={handleEnsurePageJump}
                            onKeyDown={onKeyDown}
                            onBlur={handleEnsurePageJump}
                            onChange={setPageJump}
                            align="center"
                        />
                        <span>/ {Math.ceil(total! / pageSize)}</span>
                    </div>
                    ) : renderPageButtons({...buttonProps, iconBtn: false})
            }

            // 根据 item.type 获取对应的 props 值
            let displayValue = true;
            switch (item.type) {
                case 'first': displayValue = first; break;
                case 'prev': displayValue = prev; break;
                case 'next': displayValue = next; break;
                case 'last': displayValue = last; break;
                default: displayValue = true;
            }

            return getPaginationButtonAdapter({
                ...buttonProps,
                display: displayValue,
                type: item.type,
                children: item.children,
                simple: simple,
                key: item.type,
                className: simple ? `${clsPrefix}-${item.type}-simple ${clsPrefix}-${item.type}-simple-${dir}` : `${clsPrefix}-${item.type}`,
                title: local.langMap[item.type] || item.title,
                id: id && `${id}-${item.type}`,
                fieldid: fieldid && `${fieldid}-${item.type}`
            })
        })
    };

    // render 函数逻辑
    const {jumpPageState, items: stateItems, pageSize: statePageSize, activePage: stateActivePage, loading} = state;
    // sizeChangerProps 属性过滤，未配置属性不可渲染
    const newSizeChangerProps = useMemo(() => {
        let result: any = {};
        for (let key in sizeChangerProps) {
            if (sizeChangerPropsMap.includes(key)) result[key] = sizeChangerProps[key as keyof typeof sizeChangerProps];
        }
        return result;
    }, [sizeChangerProps]);

    if (hideOnSinglePage && stateItems === 1) return null; // hideOnSinglePage为true时,只有一页不显示分页器

    const btnDom = confirmBtn ? confirmBtn(props) : null;

    const classNames = classnames(`${clsPrefix}-list`, {
        [`${clsPrefix}-no-border`]: noBorder,
        [`${clsPrefix}-${size}`]: size,
        [`${clsPrefix}-gap`]: !noBorder && gap,
        [`${clsPrefix}-with-jumpbtn`]: !!btnDom,
    });
    const wrapperClass = classnames(clsPrefix, className, {
        [`${clsPrefix}-simple`]: !!simple,
        [`${clsPrefix}-disabled`]: disabled,
        [`${clsPrefix}-rtl`]: dir === 'rtl',
    });
    /**
     *  页按钮属性
     *  onSelect:暴露在外层交互动作，也是与父组件Pagination的交流接口
     *  componentClass: 用户定义的按钮dom元素类型
     *  showTitle: 用户自定义定义title
     */
    const buttonProps: PaginationButtonAdapterPublic = {
        onChange: onPageBtnSelect,
        pageSize: statePageSize,
        itemRender,
        componentClass: buttonComponentClass,
        iconBtn: true,
        showTitle,
    };
    const jumpBtn = btnDom ? (
        <div id={id && `${id}-jump-btn`} fieldid={fieldid && `${fieldid}-jump-btn`} className="page_jump_btn" onClick={handleEnsurePageJump}>
            {btnDom}
        </div>
    ) : null;

    const liChildren = renderPageList(buttonProps, lang);
    let adapterNid = getNid(props)
    return (
        <div id={id} fieldid={fieldid} style={style} className={wrapperClass} {...omit(others, ["defaultDataNum", "onSelect"])} {...adapterNid}>
            {disabled && <div className={`${clsPrefix}-disabled-mask`}/>}
            <ul className={classNames}>
                {liChildren}
            </ul>
            {
                total !== null && !simple ? (
                    <div id={id && `${id}-total`} fieldid={fieldid && `${fieldid}-total`} className={`${clsPrefix}-total`}>
                        {
                            loading ? <span>{lang.langMap.loading || '加载中...'}</span> :
                                showTotal ? showTotal(total, [total === 0 ? 0 : (stateActivePage - 1) * statePageSize + 1, stateActivePage * statePageSize > total ? total : stateActivePage * statePageSize]) :
                                    <>{lang.langMap.total || '共'}<span>{total}</span>{lang.langMap.items || '条'}</>
                        }
                    </div>
                ) : null
            }

            {
                showSizeChanger || (typeof simple !== 'boolean' && simple?.showSizeChanger) ? (
                    <div className={`data_per_select`}>
                        <Select
                            id={id && `${id}-size-changer`}
                            fieldid={fieldid && `${fieldid}-size-changer`}
                            mode={pageSizeInput ? "combobox" : undefined}
                            dropdownAlign={dropdownAlign}
                            getPopupContainer={getPopupContainer}
                            value={selectFocus ? null : statePageSize}
                            open={selectOpen}
                            onClick={handleSelectClick}
                            onInputKeyDown={handleInputKeyDown}
                            onBlur={handleSelectBlur}
                            onFocus={handleSelectFocus}
                            onSelect={dataNumSelect}
                            onChange={handleSelectChange}
                            listHeight={true}
                            virtual={false}
                            placeholder={selectFocus ? statePageSize : null}
                            size={size === 'lg' ? 'lg' : 'xs'} // pagination只有两个size：sm=22和lg=40，与表单组件统一尺寸不一致，需特殊处理
                            dropdownClassName={`${clsPrefix}-select-dropdown`}
                            {...newSizeChangerProps}
                        >
                            {pageSizeOptions.length > 0 &&
                                pageSizeOptions.map((item, i) => {
                                    return <Option key={i} value={item} className={selectFocus && item == statePageSize ? `${prefix}-select-item-option-selected` : undefined} >{item}</Option>
                                })}
                        </Select>
                        <span>{lang.langMap.items}</span>
                    </div>
                ) : null
            }
            {
                showQuickJumper && !simple ? (
                    <div className="page_jump">
                        <span>{lang.langMap.goto}</span>
                        <Input
                            className="page_jump_value" // 此处用于自定义输入框css样式，无需传入size
                            size={size === 'lg' ? 'lg' : 'xs'} // pagination只有两个size：sm=22和lg=40，与表单组件统一尺寸不一致，需特殊处理
                            // autoComplete='off'
                            value={jumpPageState || ''}
                            onKeyDown={onKeyDown}
                            onPressEnter={!jumpBtn ? handleEnsurePageJump : undefined}
                            onBlur={!jumpBtn ? handleEnsurePageJump : undefined}
                            onChange={setPageJump}
                            id={id && `${id}-jump`}
                            fieldid={fieldid && `${fieldid}-jump`}
                        />
                        <span>{lang.langMap.page || '页'}</span>
                        {jumpBtn}
                    </div>
                ) : null
            }

        </div>
    );

});

// 设置显示名称
Pagination.displayName = 'Pagination';

// 设置 defaultProps
Pagination.defaultProps = defaultProps;

// 添加静态方法
(Pagination as any).getCache = getCache;

// 装饰器导出格式
export default WithConfigConsumer()(
    WebUI({name: "pagination", defaultProps})(Pagination)
);